import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import {
  arrayOf, bool, func, shape, string,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import _debounce from 'lodash/debounce';

import Dropdown from '../../../dropdown';
import CategoryDropdownItem from '../CategoryDropdownItem';
import CategoryTag from '../CategoryTag';
import {
  listByParentId,
  searchByQuery,
} from '../../../../store/productCategories/actions';
import { getParentCacheKey } from '../../../../store/productCategories/helpers/getParentCacheKey';
import l10n from '../../../../shared/strings';
import {
  BACK_ITEM_ID,
  buildBrowseDropdownItems,
  mapCategoryToDropdownItem,
  toCategoryValue,
} from '../helpers/categoryDropdownItems';

const SEARCH_DEBOUNCE_MS = 300;
const VISIBLE_ITEMS_LENGTH = 8;

const getBrowseSkeletonItems = (itemsLength) => Array.from(
  { length: Math.max(1, VISIBLE_ITEMS_LENGTH - itemsLength) },
  (_, index) => ({
    _id: `__category_skeleton_${index}`,
    isSkeleton: true,
  }),
);

const CategoriesDropdown = ({
  checkedItems,
  onSelect,
  onClear,
  disabled,
  filterText,
}) => {
  const dispatch = useDispatch();
  const [parentStack, setParentStack] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchCacheRef = useRef({ query: '', items: [] });
  const browseItemsRef = useRef([]);
  const activeQueryRef = useRef('');
  const pendingSearchRef = useRef(null);

  const abortPendingSearch = useCallback(() => {
    pendingSearchRef.current?.abort?.();
    pendingSearchRef.current = null;
  }, []);

  const currentParentId = parentStack.length
    ? parentStack[parentStack.length - 1]
    : null;
  const cacheKey = getParentCacheKey(currentParentId);

  const categories = useSelector(
    (state) => state.productCategories.childrenByParentId[cacheKey] || [],
  );
  const isLoading = useSelector(
    (state) => state.productCategories.loadingByParentId[cacheKey],
  );
  const hasError = useSelector(
    (state) => state.productCategories.errorByParentId[cacheKey],
  );

  const loadLevel = useCallback((parentId) => {
    dispatch(listByParentId({ parentId }));
  }, [dispatch]);

  useEffect(() => {
    loadLevel(currentParentId);
  }, [currentParentId, loadLevel]);

  const browseItems = useMemo(() => buildBrowseDropdownItems({
    categories,
    showBack: parentStack.length > 0,
    backTitle: l10n.PRODUCT_INFO.category.back,
  }), [categories, parentStack.length]);

  const dropdownItems = useMemo(() => {
    if (!isLoading) return browseItems;
    return [...browseItems, ...getBrowseSkeletonItems(browseItems.length)];
  }, [browseItems, isLoading]);

  browseItemsRef.current = browseItems;

  const runSearch = useCallback(async (query) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      searchCacheRef.current = { query: '', items: [] };
      return browseItemsRef.current;
    }

    abortPendingSearch();

    try {
      const request = dispatch(searchByQuery({ query: trimmedQuery }));
      pendingSearchRef.current = request;
      const results = await request.unwrap();
      pendingSearchRef.current = null;

      const items = (results || [])
        .map((category) => mapCategoryToDropdownItem(category, { isSearchResult: true }));

      searchCacheRef.current = { query: trimmedQuery, items };
      return items;
    } catch (error) {
      pendingSearchRef.current = null;
      return [];
    }
  }, [abortPendingSearch, dispatch]);

  const debouncedSearchRef = useRef(
    _debounce(async (query, resolve) => {
      try {
        const items = await runSearch(query);
        if (activeQueryRef.current !== query) return;
        resolve(items);
      } catch (error) {
        if (activeQueryRef.current !== query) return;
        resolve([]);
      } finally {
        if (activeQueryRef.current === query) {
          setIsSearching(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS),
  );

  useEffect(() => () => {
    debouncedSearchRef.current.cancel();
    abortPendingSearch();
  }, [abortPendingSearch]);

  const filterFunc = useCallback((query) => new Promise((resolve) => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      activeQueryRef.current = '';
      debouncedSearchRef.current.cancel();
      abortPendingSearch();
      searchCacheRef.current = { query: '', items: [] };
      setIsSearching(false);
      resolve(browseItemsRef.current);
      return;
    }

    activeQueryRef.current = trimmedQuery;
    setIsSearching(true);
    debouncedSearchRef.current(trimmedQuery, resolve);
  }), [abortPendingSearch]);

  const handleSelect = useCallback((item) => {
    if (!item || item.isSkeleton) return;

    if (item.isBack || item._id === BACK_ITEM_ID) {
      setParentStack((stack) => stack.slice(0, -1));
      return;
    }

    if (item.isExpandable) {
      setParentStack((stack) => [...stack, item._id]);
      return;
    }

    const categoryValue = toCategoryValue(item.category || item);
    if (!categoryValue) return;

    onSelect(categoryValue);
    setParentStack([]);
  }, [onSelect]);

  const handleUncheck = useCallback(() => {
    onClear?.();
  }, [onClear]);

  const renderCategoryTag = useCallback((tagProps) => (
    <CategoryTag {...tagProps} />
  ), []);

  const isItemsLoaded = !isLoading && !isSearching;
  const inProgress = isLoading || isSearching;

  const placeholder = useMemo(() => {
    if (hasError) return l10n.PRODUCT_INFO.category.unableToLoadCategories;
    if (!isLoading && !categories.length && !parentStack.length && !checkedItems.length) {
      return l10n.PRODUCT_INFO.category.categoriesBeingPrepared;
    }
    return l10n.PRODUCT_INFO.category.searchCategories;
  }, [categories.length, checkedItems.length, hasError, isLoading, parentStack.length]);

  const resolvedFilterText = filterText || l10n.PRODUCT_INFO.category.searchCategories;

  return (
    <Dropdown
      items={dropdownItems}
      checkedItems={checkedItems}
      type="keyword"
      showChips
      showDeletedOptions
      showAddButton={false}
      canCreate={false}
      disabled={disabled}
      filterText={resolvedFilterText}
      placeholder={placeholder}
      createHandler={null}
      visibleItemsLength={VISIBLE_ITEMS_LENGTH}
      isItemsLoaded={isItemsLoaded}
      inProgress={inProgress}
      filterFunc={filterFunc}
      additionalClass="categoryDropdown"
      itemComponent={CategoryDropdownItem}
      tagRenderer={renderCategoryTag}
      noResultsText={l10n.PRODUCT_INFO.category.noCategoriesFound}
      onCheckedHandler={handleSelect}
      onUncheckedHandler={handleUncheck}
      onDropdownOpenClickHandler={() => {}}
    />
  );
};

CategoriesDropdown.defaultProps = {
  checkedItems: [],
  onClear: null,
  disabled: false,
  filterText: null,
};

CategoriesDropdown.propTypes = {
  checkedItems: arrayOf(shape({
    _id: string,
    title: string,
  })),
  onSelect: func.isRequired,
  onClear: func,
  disabled: bool,
  filterText: string,
};

export default CategoriesDropdown;
